/**
 * Fan-side "我擁有的物件" panel embedded inside CharacterMarket.
 *
 * v1 surface:
 *   - List the fan's purchased objects
 *   - One-tap "套用到 {character}" — attaches with a sensible default
 *     transform (origin, identity rotation, scale 1) and either no
 *     bone (kind=static) or the first bone matching a head-region
 *     heuristic
 *   - "已附加" badge + 解除 button for objects already on this character
 *
 * Rich transform editing (drag-bind, sliders, bone picker dropdown,
 * gizmo) is a follow-up — porting admin-ui/src/pages/edit/SceneObjects.tsx
 * is a substantial second pass. The endpoints here are the same the
 * full editor will use later, so the data plumbing is forward-compat.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listMyFanObjects,
  attachSceneObjectToCharacter,
  detachSceneObjectFromCharacter,
  getPublicInfo,
  type OwnedObject,
  type PublicCharacterInfo,
} from '../api/fan-api';

interface SceneObjectFromInfo {
  id: string;
  libraryId?: string;
  label?: string;
  kind?: 'static' | 'dynamic';
  attachedBy?: string;
  attachment?: { boneName: string };
}

export function OwnedObjectsPanel({ characterId, characterName, ownedByFan }: {
  characterId: string;
  characterName: string;
  /** True iff the current viewer is the fan that owns the
   *  character's skin. Without ownership the panel is hidden. */
  ownedByFan: boolean;
}) {
  const [objects, setObjects] = useState<OwnedObject[] | null>(null);
  const [publicInfo, setPublicInfo] = useState<PublicCharacterInfo | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!ownedByFan) return;
    let alive = true;
    void listMyFanObjects()
      .then((r) => { if (alive) setObjects(r.items); })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    void getPublicInfo(characterId)
      .then((p) => { if (alive) setPublicInfo(p); })
      .catch(() => { /* ignore — attach still works without the bone list */ });
    return () => { alive = false; };
  }, [characterId, ownedByFan, refreshKey]);

  if (!ownedByFan) return null;

  // Map of attached scene-object id → SceneObject row (so we can show
  // 已附加 badges + detach buttons).
  const attached = ((publicInfo?.model3d?.sceneObjects as SceneObjectFromInfo[] | undefined) ?? []);
  const attachedByLibraryId = new Map<string, SceneObjectFromInfo>();
  for (const s of attached) {
    if (s.libraryId) attachedByLibraryId.set(s.libraryId, s);
  }

  async function attach(obj: OwnedObject): Promise<void> {
    if (busy) return;
    setBusy(obj.objectId);
    setErr(null);
    try {
      await attachSceneObjectToCharacter(characterId, {
        objectId: obj.objectId,
        label: obj.label ?? '我的物件',
        kind: 'static',
        transform: {
          position: [0, 0.5, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
        },
        visibility: 'always',
      });
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function detach(sceneObjectId: string): Promise<void> {
    if (busy) return;
    setBusy(sceneObjectId);
    setErr(null);
    try {
      await detachSceneObjectFromCharacter(characterId, sceneObjectId);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-200">我擁有的物件</h2>
        <Link to="/objects" className="text-xs text-violet-300 hover:text-violet-200">前往物件庫 →</Link>
      </div>

      {err && (
        <div role="alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200">{err}</div>
      )}

      {!objects && <p className="text-xs text-slate-500">載入中…</p>}

      {objects && objects.length === 0 && (
        <p className="text-xs text-slate-500">
          你還沒有任何物件。
          <Link to="/objects" className="ml-1 text-violet-300 hover:text-violet-200">逛物件庫</Link>。
        </p>
      )}

      {objects && objects.length > 0 && (
        // Scroll within the panel once we exceed ~4 rows so the page
        // below (FeatureTiles, the upcoming Phase B affinity card)
        // doesn't get pushed off the fold by power-users with large
        // inventories.
        <ul className="space-y-1.5 max-h-72 overflow-y-auto -mx-1 px-1">
          {objects.map((obj) => {
            const att = attachedByLibraryId.get(obj.objectId);
            return (
              <li key={obj.objectId} className="flex items-center gap-2 text-sm border border-slate-800 rounded-lg p-2.5">
                <div className="text-xl shrink-0" aria-hidden="true">📦</div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-100 truncate" title={obj.label}>
                    {obj.label ?? <span className="font-mono text-xs text-slate-400">{obj.objectId.slice(0, 8)}…</span>}
                  </div>
                  {att ? (
                    <div className="text-[10px] text-blue-400 mt-0.5">已附加在「{characterName}」上</div>
                  ) : (
                    <div className="text-[10px] text-slate-500 mt-0.5">已擁有，尚未附加</div>
                  )}
                </div>
                {att && att.attachedBy ? (
                  <button
                    type="button"
                    onClick={() => { void detach(att.id); }}
                    disabled={busy === att.id}
                    className="shrink-0 rounded-lg border border-slate-700 bg-slate-800/60 hover:bg-slate-800 text-slate-200 px-3 py-2 text-xs font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-slate-500 transition"
                  >{busy === att.id ? '處理中…' : '解除'}</button>
                ) : att ? (
                  <span className="shrink-0 text-[10px] text-slate-500">由經營者附加</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => { void attach(obj); }}
                    disabled={busy === obj.objectId}
                    className="shrink-0 rounded-lg bg-violet-500 hover:bg-violet-400 text-white px-3 py-2 text-xs font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-violet-300 transition active:scale-[0.97]"
                  >{busy === obj.objectId ? '處理中…' : '套用'}</button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[10px] text-slate-500 leading-relaxed">
        套用後物件會出現在 3D 互動 / 聊天頁面。位置與旋轉的細部調整（拖拉到指定骨骼、縮放）將在後續版本提供。
      </p>
    </section>
  );
}
